import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { API, withCreds, selectCls, btnSecondary, PageHeader, Spinner, money, fmtDate, errMsg, downloadCsv } from '../../components/admin/AdminUI';
import { Users, Search, Download } from 'lucide-react';
import Select from '../../components/FlatSelect';

const PAGE_SIZE = 50;

export default function AdminCustomers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', tag: '', sort: 'newest' });
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/customers`, { params: { ...filters, page, limit: PAGE_SIZE }, ...withCreds });
      setCustomers(r.data.customers);
      setTotal(r.data.total);
      setTags(r.data.tags);
    } finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); };
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <AdminLayout>
      <PageHeader title="Customers" subtitle={`${total} customer${total === 1 ? '' : 's'}`}>
        <button onClick={() => downloadCsv('/admin/customers/export', filters).catch(e => alert(errMsg(e)))} className={btnSecondary}><Download className="w-4 h-4" /> Export CSV</button>
      </PageHeader>

      <div className="admin-card rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/45" />
          <input value={searchText} onChange={e => setSearchText(e.target.value)} onKeyDown={e => e.key === 'Enter' && setFilter('search', searchText)}
            onBlur={() => searchText !== filters.search && setFilter('search', searchText)}
            placeholder="Name, email, or phone…"
            className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl pl-10 pr-4 py-2 text-ink text-sm focus:outline-none focus:border-honey" />
        </div>
        {tags.length > 0 && (
          <Select value={filters.tag} onChange={e => setFilter('tag', e.target.value)} className={selectCls}>
            <option value="">All tags</option>
            {tags.map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
        )}
        <Select value={filters.sort} onChange={e => setFilter('sort', e.target.value)} className={selectCls}>
          <option value="newest">Newest</option><option value="oldest">Oldest</option><option value="name">Name</option>
          <option value="spent">Top spenders</option><option value="orders">Most orders</option><option value="last_order">Recent buyers</option>
        </Select>
      </div>

      <div className="admin-card rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-ink/10">
            <tr className="text-ink/55 text-xs uppercase tracking-widest">
              <th className="text-left p-3">Customer</th>
              <th className="text-left p-3">Orders</th>
              <th className="text-left p-3">Total spent</th>
              <th className="text-left p-3">Last order</th>
              <th className="text-left p-3">Joined</th>
              <th className="text-left p-3">Tags</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/10">
            {loading ? <tr><td colSpan={6}><Spinner /></td></tr> : customers.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12">
                <Users className="w-8 h-8 text-ink/30 mx-auto mb-2" />
                <p className="text-ink/45 text-sm">No customers found</p>
              </td></tr>
            ) : customers.map(c => (
              <tr key={c.id} onClick={() => navigate(`/admin/customers/${c.id}`)} className="text-ink/75 hover:bg-ink/[0.04] cursor-pointer">
                <td className="p-3">
                  <p className="text-ink font-medium">{c.name || '—'}</p>
                  <p className="text-ink/55 text-xs">{c.email}</p>
                </td>
                <td className="p-3">{c.order_count}</td>
                <td className="p-3 font-bold text-ink">{money(c.total_spent)}</td>
                <td className="p-3 text-ink/60 text-xs">{fmtDate(c.last_order_at)}</td>
                <td className="p-3 text-ink/60 text-xs">{fmtDate(c.created_at)}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {c.tags.map(t => <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-honey/10 text-honey-700">{t}</span>)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className={btnSecondary}>Prev</button>
          <span className="text-ink/55 text-sm">Page {page} of {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages} className={btnSecondary}>Next</button>
        </div>
      )}
    </AdminLayout>
  );
}
